'use client';
import qs from 'qs';
import { Box } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import xhr from 'xhr';
import Boletin from './boletin';

// Año del semestre: primero name, después start_date y por último el año actual.
const yearFromSemester = (sem) => {
  const s = sem?.attributes || sem;
  const match = String(s?.name || '').match(/20\d{2}/);
  if (match) return match[0];
  if (s?.start_date) {
    const year = new Date(s.start_date).getUTCFullYear();
    if (!Number.isNaN(year)) return String(year);
  }
  return String(new Date().getFullYear());
};
function ModalFinalResults({ idGrade, idEst, student, resultsData }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Anio del boletin: sale del semestre de la matricula del estudiante.
  const [certYear, setCertYear] = useState(String(new Date().getFullYear()));
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [enrollmentError, setEnrollmentError] = useState(false);
  // Nombres (normalizados) de materias extra, para NO imprimirlas en el certificado.
  const [extraNames, setExtraNames] = useState(() => new Set());

  const calculateAverageScores = (notesArray) => {
    const subjectScores = {};

    // Normalizar el nombre de cada materia y agrupar las notas
    notesArray.forEach(({ notes }) => {
      notes.forEach(({ score, subject, intensity }) => {
        if (subject) {
          // Normalizar el nombre quitando la parte con el número al final
          const normalizedSubject = subject.replace(/\s\d.*$/, '');

          if (!subjectScores[normalizedSubject]) {
            subjectScores[normalizedSubject] = { totalScore: 0, count: 0, intensity: intensity };
          }
          subjectScores[normalizedSubject].totalScore += Number(score);
          subjectScores[normalizedSubject].count += 1;
        }
      });
    });

    // Transformar el objeto en un array de { name, note }
    const averageScoresArray = Object.keys(subjectScores).map((subject) => {
      const { totalScore, count, intensity } = subjectScores[subject];

      return {
        name: subject,
        note: Number(totalScore) / count,
        intensity: intensity,
      };
    });

    return averageScoresArray;
  };

  useEffect(() => {
    (async () => {
      if (idEst) {
        setLoading(true);
        setLoadError('');
        try {

        const res = await xhr(`/student-notes?filters[student][id][$eq]=${idEst}&filters[grade][id][$eq]=${idGrade}`);
        const data = res.data.data;

        const sortedNotes = data.sort((a, b) => {
          if (a.period === 'primer') return -1; // "primer" va primero
          if (b.period === 'primer') return 1;
          return 0; // Mantén el orden para otros valores
        });

        // Resultado
        const averageScores = calculateAverageScores(sortedNotes);

        const getNames = averageScores?.map((el) => el.name);

        const query = qs.stringify(
          {
            filters: {
              title: {
                $in: getNames,
              },
              grade: {
                id: {
                  $eq: idGrade,
                },
              },
              final: {
                $eq: true,
              },
            },
          },
          {
            encodeValuesOnly: true,
          }
        );

        const query2 = qs.stringify(
          {
            filters: {
              subject: {
                $in: getNames,
              },
              student: {
                id: {
                  $eq: idEst,
                },
              },
            },
          },
          {
            encodeValuesOnly: true,
          }
        );

        const resExam = await xhr(`/quiz-modals?${query}`);
        const dataExam = resExam.data.data;

        const insertExam2 = averageScores.map((el) => ({
          ...el,
          exam: dataExam.find((exam) => exam.title === el.name),
        }));

        const sittingExam = await xhr(`/quiz-modal-sittings?${query2}`);
        const dataSitting = sittingExam.data.data;

        const insertSitting = insertExam2.map((el) => ({
          ...el,
          sitting: dataSitting.find((sitting) => sitting.subject === el.name),
        }));

        // Anio del boletin: tomar el semestre de la matricula del estudiante.
        try {
          const resEnr = await xhr(
            `/grade-enrollments?filters[student][id][$eq]=${idEst}&filters[grade][id][$eq]=${idGrade}&populate=semester&sort[0]=createdAt:desc&pagination[limit]=1`
          );
          const enrollment = resEnr?.data?.data?.[0];
          if (!enrollment?.status) throw new Error('No se encontró el estado de la matrícula');
          setEnrollmentStatus(enrollment.status);
          const sem = enrollment.semester;
          if (sem) setCertYear(yearFromSemester(sem));
        } catch (e) {
          setEnrollmentError(true);
        }

        // Materias extra: no se imprimen en el certificado final.
        try {
          const resExtra = await xhr(
            `/subject-enrollments?filters[student][id][$eq]=${idEst}&filters[is_extra][$eq]=true&pagination[limit]=-1&populate=subject`
          );
          const extrasData = resExtra?.data?.data || [];
          setExtraNames(
            new Set(extrasData.map((e) => (e?.subject?.name || '').replace(/\s\d.*$/, '')))
          );
        } catch (e) {
          // si falla, no excluimos extras
        }

        setNotes(insertSitting);

        } catch (error) {
          setLoadError(error.response?.status === 403
            ? 'Tu usuario no tiene permiso para consultar los datos de las notas finales. Solicita revisar los permisos de tu rol.'
            : 'No se pudieron cargar las notas finales. Cierra esta ventana y vuelve a intentarlo.');
        } finally {
          setLoading(false);
        }
      }
    })();
  }, []);

  return (
    <div className="">
      <p className="dark:text-white">Ponderado de notas</p>

      <hr />

      {loadError ? <p role="alert">{loadError}</p> : enrollmentError ? <p>No se pudo consultar la matrícula para generar el certificado. Cierra y vuelve a abrir las notas finales.</p> : <Boletin
        dataResults={notes}
        grade={resultsData[0]?.subject?.grade?.name}
        student={student}
        loadingData={loading}
        extraNames={extraNames}
        year={certYear}
        enrollmentStatus={enrollmentStatus}
      />}

      <div className="mt-3">
        {loading ? (
          <p>Cargando...</p>
        ) : (
          notes.map((el, index) => {
            // Nota final: max(recuperación topada a 6.5, nota del año).
            // Coincide con el modal del estudiante, el boletín y el certificado.
            const noteFinal = el.sitting && el.sitting.note != null
              ? Math.max(Math.min(Number(el.sitting.note) / 10, 6.5), Number(el.note) || 0)
              : (Number(el.note) || 0);
            return (
              <Box key={el.index} display="flex">
                <p>
                  {el.name}: <b>{noteFinal.toFixed(2)}</b> - {noteFinal < 6.5 ? 'Perdida' : 'Aprobada'}
                </p>
                {el.note < 6.5 && el.exam && (
                  <div>
                    {el.sitting ? (
                      <div style={{ marginLeft: 4 }}>
                        (Ya el estudiante presentó este examen, nota):{' '}
                        <b>{noteFinal.toFixed(2)}</b>
                      </div>
                    ) : (
                      <div>No ha presentado recuperacion</div>
                    )}
                  </div>
                )}
              </Box>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ModalFinalResults;
